/**
 * The reminder templates a new company starts with (Collection OS Phase 0;
 * docs/product/phase-0/02).
 *
 * A company should be able to send its first reminder on its first day, in
 * words it would be comfortable putting its name to. These are copied into
 * each company (0037 `seed_default_message_templates`), where they are the
 * company's own to edit, archive or delete; nothing global changes when a
 * company edits its copy, and a company that already has templates is never
 * given these.
 *
 * The wording is neutral, professional B2B English: no threats, no legal
 * escalation, no "final notice". Every message offers the customer a way to
 * say they have already paid. Placeholders are the fixed allow-list in
 * `templateVariables.ts`; the greeting never depends on an optional field, so
 * a message reads properly when a contact name is not recorded.
 *
 * Held equal to the SQL in supabase/migrations/0037 by
 * packages/domain/tests/defaultTemplates.test.ts.
 */
import type { TemplateChannel } from './templateVariables.js';

export interface DefaultTemplate {
    name: string;
    channel: Exclude<TemplateChannel, 'internal'>;
    /** The one a screen offers first for its channel. */
    isDefault: boolean;
    content: string;
}

export const DEFAULT_MESSAGE_TEMPLATES: readonly DefaultTemplate[] = [
    {
        name: 'Payment reminder',
        channel: 'whatsapp',
        isDefault: true,
        content: [
            'Hello,',
            '',
            'This is a reminder from {{company_name}} about the account of {{customer_name}}. Our records show an outstanding balance of {{outstanding_amount}}.',
            '',
            'If you have already made the payment, please share the payment details so that we can update our records.',
            '',
            'Thank you,',
            '{{company_name}}',
        ].join('\n'),
    },
    {
        name: 'Payment due today',
        channel: 'whatsapp',
        isDefault: false,
        content: [
            'Hello,',
            '',
            'A reminder from {{company_name}}: a payment on the account of {{customer_name}} is due today. The outstanding balance is {{outstanding_amount}}.',
            '',
            'Please let us know once the payment has been made, or share the payment details if it has already been paid.',
            '',
            'Thank you,',
            '{{company_name}}',
        ].join('\n'),
    },
    {
        name: 'Overdue balance',
        channel: 'whatsapp',
        isDefault: false,
        content: [
            'Hello,',
            '',
            'Our records show an outstanding balance of {{outstanding_amount}} on the account of {{customer_name}}, some of which is now past its due date.',
            '',
            'Could you please let us know when we can expect the payment? If it has already been made, please share the details and we will update our records.',
            '',
            'Thank you,',
            '{{company_name}}',
        ].join('\n'),
    },
    {
        name: 'Promised payment due today',
        channel: 'whatsapp',
        isDefault: false,
        content: [
            'Hello,',
            '',
            'Thank you for confirming the payment of {{expected_amount}} for {{promised_date}}. This is a gentle reminder from {{company_name}} that it is due today.',
            '',
            'Please share the payment details once it has been made.',
            '',
            'Thank you,',
            '{{company_name}}',
        ].join('\n'),
    },
    {
        name: 'Promised payment not received',
        channel: 'whatsapp',
        isDefault: false,
        content: [
            'Hello,',
            '',
            'We have not yet received the payment of {{expected_amount}} that was expected on {{promised_date}} for the account of {{customer_name}}. The outstanding balance is {{outstanding_amount}}.',
            '',
            'Could you please let us know when we can expect it? If it has already been paid, please share the details so that we can update our records.',
            '',
            'Thank you,',
            '{{company_name}}',
        ].join('\n'),
    },
    {
        name: 'Account follow-up (email)',
        channel: 'email',
        isDefault: true,
        content: [
            'Dear Sir/Madam,',
            '',
            'This is a reminder from {{company_name}} about the account of {{customer_name}}.',
            '',
            'Outstanding balance: {{outstanding_amount}}',
            '',
            'We would be grateful if you could review the account and let us know when we can expect the payment. If any invoice is under query, please tell us so that we can resolve it promptly.',
            '',
            'If you have already made the payment, please share the payment details, and thank you.',
            '',
            'Regards,',
            '{{company_name}}',
        ].join('\n'),
    },
];
